import axios from "axios";

function getUniqueId() {
  const os = getOSInfo();
  const { browser, buildNo } = getBrowserInfo();

  return `${os}/${browser}-${buildNo}`
}

function getOSInfo() {
  const platform = navigator?.platform || "";

  if (platform.includes("Win")) return "win";
  if (platform.includes("Mac")) return "mac";
  if (platform.includes("Linux")) return "linux";
  if (/Android/i.test(navigator.userAgent)) return "android";
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) return "ios";
  return "anony";
}

function getBrowserInfo() {
  const userAgent = navigator?.userAgent || "";
  let browser, version, buildNo;

  if (userAgent.indexOf("Chrome") > -1 && userAgent.indexOf("Edge") === -1 && userAgent.indexOf("OPR") === -1) {
    browser = "chrome";
    version = userAgent.substring(userAgent.indexOf("Chrome") + 7);
    buildNo = version.split(' ')?.[0];
  } else if (userAgent.indexOf("Firefox") > -1) {
    browser = "fox";
    version = userAgent.substring(userAgent.indexOf("Firefox") + 8);
    buildNo = version.split(' ')?.[0];
  } else if (userAgent.indexOf("Safari") > -1 && userAgent.indexOf("Chrome") === -1) {
    browser = "safari";
    version = userAgent.substring(userAgent.indexOf("Version") + 8);
    buildNo = version.split(' ')?.[0];
  } else if (userAgent.indexOf("MSIE") > -1 || userAgent.indexOf("Trident") > -1) {
    browser = "explorer";
    version = userAgent.substring(userAgent.indexOf("MSIE") + 5);
    buildNo = version.split(' ')?.[0];
  } else if (userAgent.indexOf("Edge") > -1) {
    browser = "edge";
    version = userAgent.substring(userAgent.indexOf("Edge") + 5);
    buildNo = version.split(' ')?.[0];
  } else {
    browser = "anony";
    version = "nan";
    buildNo = "nan";
  }

  return {
    browser,
    version,
    buildNo
  };
}

export const isHybrid = (url) => {
  const urls = [
    /^\/faq/,
    /^\/viewer/,
    /^\/about-us/,
    /^\/contact-us/,
    /^\/privacy-policy/,
    /^\/sharia-committee/,
    /^\/submit-opportunities/,
    /^\/investment-calculator/,
    /^\/opening-agreement/,
    /^\/conflict-policy/,
    /^\/maintenance/,
    /^\/network-error/,
    /^\/delete-account/,
    /^\/delete-account-old/
  ];
  return urls.some((path) => path.test(url));
};

const isHybridPages =
  typeof window !== "undefined" &&
  (window.location.pathname === "/" || isHybrid(window.location.pathname));

export const saveUserData = (user) => {
  if (typeof window !== "undefined") {
    localStorage?.setItem("user", JSON.stringify(user));
  }
};

export const getUserData = () => {
  if (typeof window !== "undefined") {
    const user = localStorage?.getItem("user");
    return JSON?.parse(user);
  }

  return null;
};

export const clearLocalStorage = () => {
  if (typeof window !== "undefined") {
    localStorage.removeItem("jwt");
    localStorage.removeItem("user");
  }
};

export const getToken = () => {
  if (typeof window !== "undefined") {
    const isFound = document.cookie
      .split('; ')
      .find(row => row.startsWith('jwt='))
      ?.split('=')[1];

    if (isFound) {
      return isFound;
    }
    return localStorage?.getItem("jwt");
  }

  return null;
};

export const http = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_APP_BASE_URL}/api/app`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Lang: "en",
  },
});
export const serverSideHttp = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_APP_BASE_URL}/api/app`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Lang: "en",
  },
});

export function getCookie(name) {
  if (typeof document !== "undefined") {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
  } else {
    return "ar";
  }
}

export function addHeaders(lang, headers = {}) {
  return {
    headers: {
      Lang: lang,
      ...headers,
    },
  };
}

let isRefreshed = false;
let failedRequests = [];
const refreshTokenUrl = "/refresh-token";

const processQueue = (error, token = null) => {
  failedRequests.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedRequests = [];
};

http.interceptors.response.use(
  (res) => res,
  async (err) => {
    const request = err.config;
    const url = request.url;
    const response = err.response;
    const status = response?.status;
    const currentPath =
      typeof window !== "undefined" ? window.location.pathname : "";

    const skipPages = [
      "/",
      "/ar",
      "/en",
      "/maintenance",
      "/ar/maintenance",
      "/en/maintenance",
      "/network-error",
      "/ar/network-error",
      "/en/network-error",
    ];

    if (skipPages.includes(currentPath)) {
      return;
    }

    if (status === 403 || status === 404) {
      if (typeof window !== "undefined") {
        window.location.replace("/network-error");
      }
      return Promise.reject(err);
    }

    if (status >= 500 && status < 600) {
      if (typeof window !== "undefined") {
        window.location.replace("/maintenance");
      }
      return Promise.reject(err);
    }

    const status401 = status === 401;
    const isRefreshToken = url === refreshTokenUrl;

    const lang = getCookie("NEXT_LOCALE");
    if (status401 && isRefreshToken && getToken()) {
      if (!isHybridPages) {
        window.location.replace(`/${lang}/login`);
      } else {
        window.location.replace(window.location.pathname);
      }
      http.post("/app-logout", undefined);
      clearLocalStorage();
      return Promise.reject(err);
    } else if (status401 && !request._retry) {
      if (isRefreshed) {
        return new Promise(function (resolve, reject) {
          failedRequests.push({ resolve, reject });
        })
          .then((jwt) => {
            request.headers.Authorization = `Bearer ${jwt}`;
            return axios(request);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      request._retry = true;
      isRefreshed = true;

      return new Promise(function (resolve, reject) {
        http
          .post(refreshTokenUrl)
          .then(({ data }) => {
            const jwt = data?.data?.token;
            window.localStorage.setItem("jwt", jwt);
            axios.defaults.headers.Authorization = `Bearer ${jwt}`;
            request.headers.Authorization = `Bearer ${jwt}`;
            processQueue(null, jwt);
            resolve(axios(request));
          })
          .catch((err) => {
            processQueue(err, null);
            reject(err);
          })
          .finally(() => {
            isRefreshed = false;
          });
      });
    }

    return Promise.reject(err);
  }
);

http.interceptors.request.use((config) => {
  const token = getToken();
  const lang = getCookie("NEXT_LOCALE");
  config.headers.Lang = lang;
  if (token && "/login" !== config.url) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const info = getBrowserInfo()
  // const deviceID = getUniqueId()

  config.headers.platform = "web"
  // config.headers.deviceID = deviceID;
  config.headers.build = info.buildNo;
  config.headers.version = info.version;
  return config;
});
